import { Button, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
  ButtonContainer,
  ButtonsBloc,
  DataTable,
  FGCustomPanel,
  FGMaskInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
  showError,
  useGridState,
  useSearchApi
} from "nsitools-react";
import * as React from "react";
import { useHistory, useLocation } from "react-router";
import {
  ChefEntrepriseApi,
  ChefsEntrepriseDoublonGridDto,
  ChefsEntrepriseDoublonSearchDto,
  ChefsEntrepriseDoublonSearchDtoFromJSON
} from "../../../../../api";
import { ERoutes } from "../../../../../AppRouter";
import {
  AddButton,
  CancelButton,
  EditButton,
  FGWalterDateMaskInput,
  FGWalterSelectInput,
  SmallFormGenerator,
  UnordoredListColumn,
  ViewButton
} from "../../../../../components";
import { useBeIdCardContext, useDialog } from "../../../../../contexts";
import { useApiService, useTabMessage, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IChefEntrepriseDetailDoublonsProps {
  setBaseData: (values: ChefsEntrepriseDoublonSearchDto) => void;
}

export const ChefEntrepriseDetailDoublons: React.FunctionComponent<IChefEntrepriseDetailDoublonsProps> = ({
  setBaseData
}) => {
  const { t } = useTl();
  const dialogContext = useDialog();
  const history = useHistory();
  const { search: searchQ } = useLocation();
  const [nationalites, nLoading] = useReferential(a => a.referentialGetAllNationalite(), false, []);
  const api = useApiService(ChefEntrepriseApi);
  const { foundRowsData } = useBeIdCardContext();
  const cfs = React.useMemo(() => new URLSearchParams(searchQ).get("cfs"), [searchQ]);
  const { messageValue: baseSObj } = useTabMessage(cfs);
  const [firstSearch, setFirstSearch] = React.useState(false);
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [10],
    pageSize: 10,
    sortKeys: { nom: "ASC", prenom: "ASC" }
  });
  const { totalCount } = tableState;

  const searchFunction = React.useCallback(
    (sObj?: ChefsEntrepriseDoublonSearchDto) => {
      return api.chefEntrepriseSearchDoublonsChefsEntreprise({ ChefsEntrepriseDoublonSearchDto: sObj });
    },
    [api]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: false
  });
  const add = React.useCallback(
    (fromPersonne: boolean) => {
      if (totalCount && totalCount > 0) {
        dialogContext.showDialog({
          message: t(fromPersonne ? ETLCodes.AddFormeConfirmMessage : ETLCodes.AddConfirmMessage, {
            forme: t(ETLCodes.ChefEntreprise)
          }),
          onConfirmed: async () => {
            history.push(`${ERoutes.chefEntreprise}/-1/detail/edit${searchQ}`);
          }
        });
      } else {
        history.push(`${ERoutes.chefEntreprise}/-1/detail/edit${searchQ}`);
      }
    },
    [dialogContext, history, searchQ, t, totalCount]
  );

  const initialData = React.useMemo(() => {
    const onlyRegNatNb =
      !foundRowsData ||
      (!foundRowsData.nom && !foundRowsData.prenom && !foundRowsData.dateNaissance && foundRowsData.nationalNb);
    return ChefsEntrepriseDoublonSearchDtoFromJSON({
      nom: baseSObj?.nom?.value ?? foundRowsData?.nom,
      prenom: baseSObj?.prenom?.value ?? foundRowsData?.prenom,
      registreNational: baseSObj?.registreNational?.value ?? (onlyRegNatNb ? foundRowsData?.nationalNb : undefined),
      dateNaissance: baseSObj?.dateNaissance?.value ?? foundRowsData?.dateNaissance,
      nationalite: baseSObj?.nationalite?.value
    });
  }, [baseSObj, foundRowsData]);

  React.useEffect(() => {
    if (foundRowsData || baseSObj) {
      search(initialData);
      setFirstSearch(true);
    }
  }, [baseSObj, foundRowsData, initialData, search]);

  const columns = React.useMemo(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: ChefsEntrepriseDoublonGridDto) => (
          <ButtonContainer>
            {!!row.idchefEntreprise ? (
              <>
                <ViewButton
                  minimal={true}
                  onClick={() => history.push(`${ERoutes.chefEntreprise}/${row.idchefEntreprise}/detail/view`)}
                />
                <EditButton
                  minimal={true}
                  onClick={() => history.push(`$${ERoutes.chefEntreprise}/${row.idchefEntreprise}/detail/edit`)}
                />
              </>
            ) : (
              <AddButton
                tooltipText={t(ETLCodes.CreerAPartirdePersonne)}
                onClick={() => {
                  setBaseData({ ...row });
                  add(!row.isChefEntreprise);
                }}
              />
            )}
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Nom),
        fieldName: "nom"
      },
      {
        header: () => t(ETLCodes.Prenom),
        fieldName: "prenom"
      },
      {
        header: () => t(ETLCodes.DateNaissance),
        fieldName: "dateNaissance"
      },
      {
        header: () => t(ETLCodes.NumeroNational),
        fieldName: "registreNational"
      },
      {
        header: () => t(ETLCodes.Adresse),
        fieldName: "adresse"
      },
      {
        header: () => t(ETLCodes.CodePostal),
        fieldName: "codePostal"
      },
      {
        header: () => t(ETLCodes.Localite),
        fieldName: "localite"
      },
      {
        header: () => t(ETLCodes.Nationalite),
        fieldName: "nationalite"
      }
      // {
      //   header: () => t(ETLCodes.Forme),
      //   fieldName: "forme",
      //   render: (row: ChefsEntrepriseDoublonGridDto) => (
      //     <UnordoredListColumn
      //       values={[
      //         { value: row.isApprenant, label: t(ETLCodes.Apprenant) },
      //         { value: row.isFormateur, label: t(ETLCodes.Formateur) },
      //         { value: row.isRepresentant, label: t(ETLCodes.Representant) },
      //         { value: row.isChefEntreprise, label: t(ETLCodes.ChefEntreprise) },
      //         { value: row.isCollaborateur, label: t(ETLCodes.Collaborateur) },
      //         { value: row.isDelegueTutelle, label: t(ETLCodes.Referent) },
      //         { value: row.isConseillePedagogique, label: t(ETLCodes.ConseillerPedagogique) },
      //         { value: row.isContact, label: t(ETLCodes.Contact) }
      //       ]}
      //     />
      //   )
      // }
    ],
    [add, history, setBaseData, t]
  );

  return (
    <FieldSet collapsable={false} title={t(ETLCodes.ChefsEntreprise)}>
      <SmallFormGenerator
        enableDirtyCheck={false}
        initialValues={initialData}
        onSubmit={c => {
          if ((c.nom && c.prenom) || c.registreNational) {
            search(c);
            setFirstSearch(true);
          } else {
            showError(t(ETLCodes.NoCriteriaFilled));
          }
        }}
        editMode
        hideButtons={true}
        onCancel={() => history.push(`${ERoutes.chefEntreprise}`)}
      >
        <FieldGroup columns={2}>
          <FGTextInput name="nom" label={t(ETLCodes.Nom)} maxLength={100} forceCase="upper" />
          <FGWalterSelectInput
            name="nationalite"
            label={t(ETLCodes.Nationalite)}
            items={nationalites}
            loading={nLoading}
          />
          <FGTextInput name="prenom" label={t(ETLCodes.Prenom)} maxLength={100} />
          <FGMaskInput
            name="registreNational"
            label={t(ETLCodes.NumeroNational)}
            cleaveOptions={{ delimiters: [".", ".", "-", "."], blocks: [2, 2, 2, 3, 2], numericOnly: true }}
          />
          <FGWalterDateMaskInput name="dateNaissance" label={t(ETLCodes.DateNaissance)} />
        </FieldGroup>
        <FieldGroup>
          <FGCustomPanel>
            {ctx => (
              <InlineButtonContainer>
                <ButtonsBloc></ButtonsBloc>
                <ButtonsBloc>
                  <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.chefEntreprise}`)} />
                  <Button icon={IconNames.SEARCH} type="submit" text={t(ETLCodes.Search)} intent={Intent.PRIMARY} />
                  <AddButton
                    onClick={() => {
                      setBaseData(ctx.formik.values);
                      add(false);
                    }}
                    text={t(ETLCodes.General_Add)}
                    intent={Intent.PRIMARY}
                    minimal={false}
                    disabled={!firstSearch}
                  />
                </ButtonsBloc>
              </InlineButtonContainer>
            )}
          </FGCustomPanel>
        </FieldGroup>
        <FGCustomPanel>
          {ctx => (
            <FieldSet title={t(ETLCodes.TableResults, { count: totalCount })}>
              <DataTable dateFormat="dd-MM-yyyy" tableState={tableState} loading={loading} columns={columns} />
            </FieldSet>
          )}
        </FGCustomPanel>
      </SmallFormGenerator>
    </FieldSet>
  );
};
